// Sign-out lifecycle for browser form drafts.
//
// Signing out is the one moment a draft must not survive: the next person at this
// browser is a different session, and unsaved input is still personal data. So a
// DELIBERATE sign-out deletes this user's drafts — silently, without asking. A
// list of form names is not something a user can act on; unsaved work speaks up
// in place, through the form's own banner, or not at all (ADR 0069 §6, amended
// 2026-09-17, #501). The deliberate-close rule that DOES ask — one form, one
// dialog, at the moment its own Cancel/×/Escape/backdrop is used — lives in
// `useFormDraft.ts`'s `confirmClose()`, not here.
//
// It is called from the "Sign out" menu item's handler ONLY — never from the store's
// logout(), and never from the involuntary paths:
//
//   - the idle-timeout logout (each app's App.vue `onIdleTimeout`), and
//   - the 401 / invalid-session interceptors in each app's utils/api.ts
//
// keep calling authStore.logout() directly. Those are not decisions the user made:
// stepping away for half an hour, or a session that expired mid-request, must not
// silently destroy the form they were filling in — they sign back in and find it.
// Putting the clearing inside logout() would erase exactly that distinction, which
// is why the branch lives at the call site instead.

import { clearAllDraftsForOwner } from './formDraftStorage';
import { clearAssistantPanelState } from './assistantTransport';
import { clearNeedsYouStripHiddenForOwner } from './needsYouHidden';

/**
 * Delete every draft this user owns on this device, because they just chose to
 * sign out. Synchronous and silent — no dialog, no return value to check: the
 * caller proceeds with sign-out unconditionally, exactly as it would if this
 * user had nothing to lose.
 */
export function clearDraftsOnSignOut(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  clearAllDraftsForOwner(tenantSlug, userId);
  // The Lumi panel's open/closed state is session-scoped and just as much this
  // session's: the next sign-in in this tab starts minimized (issue 718).
  clearAssistantPanelState();
  // "Ausblenden" on the needs-you strip holds for the rest of THIS login (issue 713).
  clearNeedsYouStripHiddenForOwner(tenantSlug, userId);
}
